/**
 * Regelversion 11: Meinung prägen und Wirtschaftsordnung.
 *
 * Überzeugungsarbeit (Kommunikation): Die Regierung bespielt ein Meinungsthema über Monate. Die Stimmung
 * im Land wandert dann langsam zur eigenen Position, weiter als der normale Zeitgeist (bis ±1,6 statt ±0,9).
 * Jeder Monat kostet einen Termin. Wer gegen eine klare Mehrheit anredet, verliert in den ersten Monaten
 * Zustimmung und bewegt kaum etwas. Ist das Land stark gespalten, wirkt sie nur halb, und die Spaltung wächst.
 *
 * Wirtschaftsordnung: Ein Wert von −100 (Staatswirtschaft) bis +100 (Laissez-faire), Start 0 (Soziale
 * Marktwirtschaft). Jedes Gesetz verschiebt ihn ein wenig, die neuen Systemgesetze (content/order11.ts) stark.
 *  - Richtung Staat: weniger Wachstum, kleinere soziale Schere, Kapitalflucht, Unternehmen und ihre Verbände protestieren.
 *  - Richtung Markt: etwas mehr Wachstum, größere Schere, Streiks, mehr Risiko für Finanz- und Wirtschaftskrisen.
 *  - Ab ±50 organisiert sich ein Gegenlager. Karlsruhe prüft Eigentumsgarantie und Sozialstaatsprinzip
 *    (Klagerisiko der Gesetze), Brüssel Beihilfe- und Binnenmarktrecht (Vertragsverletzungsverfahren).
 * Das Spiel bewertet keine Richtung: Beide Wege lassen sich gehen, beide haben ihren Preis.
 * Nur im Einzelspieler.
 */
import { S, log } from './state';
import { since } from './version';
import { rnd } from './rng';
import { clamp, de } from './util';
import { pub } from './opinion';
import { OUTLETS } from '../content/parties';
import { AXN } from '../content/axes';
import { LEAN, POLAR } from '../content/axes3';
import { press7 } from './news7';
import { zgMax } from './zeitgeist';

export const woOn = (): boolean => since(11) && !S.mp;
const A = () => S as any;

// ── Überzeugungsarbeit ──

export type Pers = { ax: string; dir: 1 | -1; start: number; until: number; paused?: boolean };
export const PERS_MONTHS = 8;
export const persOn = (): boolean => since(11) && !S.mp && !!S.com;
export const pers = (): Pers | null => A().pers || null;
/** Redet man gegen eine klare Mehrheit an? */
export const againstMajority = (ax: string, dir: number): boolean => pub(ax) * dir <= -1;

/** Startet eine Kampagne für die eigene Position (kostet einen Termin, danach jeden Monat einen). */
export function comPersuade(ax: string) {
  if (!persOn() || S.phase !== 'govern' || pers() || (S.com?.pts ?? 0) < 1 || !(ax in AXN)) return;
  const gap = (S.you[ax] ?? 0) - pub(ax); if (Math.abs(gap) < .2) return;
  S.com.pts -= 1;
  const dir = gap > 0 ? 1 : -1;
  A().pers = { ax, dir, start: S.m, until: S.m + PERS_MONTHS } as Pers;
  log(`Überzeugungsarbeit: Ihr werbt ${PERS_MONTHS} Monate lang für eure Haltung zum Thema ${AXN[ax]}.${againstMajority(ax, dir) ? ' Die Mehrheit sieht es anders, das wird zäh.' : ''}`);
}
export function comPersuadeStop() { if (pers()) { log(`Ihr beendet die Kampagne zum Thema ${AXN[pers()!.ax]}.`); A().pers = null; } }

/** Monatlich nach comTick: verbraucht den Termin des Monats und verschiebt die Stimmung. */
export function persTick() {
  const P = pers(); if (!P || !persOn()) return;
  const gap = ((S.you[P.ax] ?? 0) - pub(P.ax)) * P.dir;
  if (S.m > P.until || gap <= .05) {
    log(gap <= .05 ? `Kampagne zum Thema ${AXN[P.ax]}: Die Stimmung im Land ist bei eurer Haltung angekommen.` : `Die Kampagne zum Thema ${AXN[P.ax]} ist zu Ende.`, gap <= .05 ? 'good' : '');
    A().pers = null; return;
  }
  if ((S.com.pts ?? 0) < 1) { P.paused = true; return; } // kein Termin frei: Pause in diesem Monat
  S.com.pts -= 1; P.paused = false;
  const t = S.m - P.start, against = againstMajority(P.ax, P.dir), polar = A().polar || 0, hi = polar >= 35;
  let k = .07 * (hi ? .5 : 1) * (against && t < 3 ? .3 : 1);
  k = Math.min(k, gap);
  const z = (S.zg ??= {}); z[P.ax] = clamp((z[P.ax] || 0) + P.dir * k, -zgMax(), zgMax());
  if (against && t < 3) { for (const g in S.bonus) S.bonus[g] -= .12; S.press = clamp((S.press || 0) - .2, -6, 6); }
  if (hi) {
    A().polar = clamp(polar + 1.2, 0, 100);
    const lean: any = POLAR.has(P.ax) ? (LEAN as any)[P.ax] || {} : {};
    for (const g in lean) if (Math.sign(lean[g]) === -P.dir) S.bonus[g] -= .1; // die andere Seite verhärtet sich
  }
}

// ── Wirtschaftsordnung ──

export const wo = (): number => A().wo ?? 0;
export function woLabel(v = wo()): string {
  return v <= -60 ? 'Staatswirtschaft' : v <= -25 ? 'Starker Sozialstaat' : v < 25 ? 'Soziale Marktwirtschaft' : v < 60 ? 'Wirtschaftsliberal' : 'Laissez-faire';
}
/** Wirkung eines Gesetzes auf die Wirtschaftsordnung: ausdrücklich (Systemgesetze) oder aus seinen Haltungen. */
export function woOf(fx: any): number {
  if (fx?.wo != null) return fx.wo;
  const p = fx?.pos || {};
  const d = -((p.sozial || 0) * 1.2 + (p.steuern || 0) * 1.2 + (p.wohnen || 0) * .8 + (p.mindestlohn || 0) * .8 + (p.buergerv || 0) * .6) + (p.buero || 0) + (p.buergergeld || 0) * .6;
  return Math.round(clamp(d, -4, 4) * 10) / 10;
}

/** Beim Inkrafttreten (govern.ts, enact). */
export function woEnact(L: any) {
  if (!woOn() || L.rep) return;
  const d = woOf(L.fx); if (!d) return;
  const before = wo(); L.wo = d; A().wo = clamp(before + d, -100, 100);
  if (woLabel(before) !== woLabel()) log(`Die Wirtschaftsordnung verschiebt sich: Deutschland gilt jetzt als ${woLabel()}.`, '');
  for (const k in L.fx?.hit || {}) { const st = S.issues[k]; if (st?.on) st.sev = clamp(st.sev - L.fx.hit[k], 0, 100); }
  if (L.fx?.eu && rnd() < L.fx.eu) (A().woEu ??= []).push({ t: L.t, m: S.m + 4 + Math.floor(rnd() * 4) });
}
/** Gesetz aufgehoben oder in Karlsruhe gekippt (court.ts, revertLaw). */
export function woRevert(L: any) {
  if (!L?.wo) return;
  A().wo = clamp(wo() - L.wo, -100, 100); L.wo = 0;
  A().woEu = (A().woEu || []).filter((x: any) => x.t !== L.t);
}

/** Beitrag zum Wachstumsziel (Prozentpunkte). */
export const woGr = (): number => (woOn() ? (wo() > 0 ? wo() * .003 : wo() * .007) : 0);
/** Beitrag zum Ziel der sozialen Schere (Gini-Punkte). */
export const woGini = (): number => (woOn() ? wo() * .035 : 0);

/** Monatlich in der Regierungszeit. */
export function woTick(): boolean {
  if (!woOn() || S.phase !== 'govern') return false;
  const v = wo(); let att = false;
  if (v < -35) {
    const x = -v - 35;
    A().flightU = Math.min((A().flightU || 0) + x * .0015, Math.max(A().flightU || 0, x * .08)); // Kapitalflucht
    S.bonus.unt -= x * .002; if (S.lob) S.lob.arbg = clamp((S.lob.arbg ?? 50) - x * .01, 0, 100);
    if (rnd() < x * .0008) { log('Die Wirtschaftsverbände rufen zum Protest auf: Investitionen werden gestoppt, Konzerne drohen mit dem Wegzug.', 'bad'); press7(OUTLETS[1].n, 'Wirtschaft schlägt Alarm: „Dieser Kurs vertreibt Investoren“', -.6); S.shockGr -= .08; att = true; }
  }
  if (v > 35) {
    const x = v - 35;
    S.bonus.arb -= x * .002; S.bonus.fam -= x * .001; if (S.lob) { S.lob.gew = clamp((S.lob.gew ?? 50) - x * .01, 0, 100); S.lob.soz = clamp((S.lob.soz ?? 50) - x * .008, 0, 100); }
    if (rnd() < x * .0012) { log('Streiks und Proteste: Gewerkschaften und Sozialverbände gehen gegen den Abbau von Schutzrechten auf die Straße.', 'bad'); press7(OUTLETS[2].n, 'Hunderttausende demonstrieren gegen den Marktkurs der Regierung', -.6); S.shockGr -= .05; att = true; }
    else if (rnd() < x * .0004) { log('Eine Spekulationsblase platzt. Ohne Aufsicht und Puffer trifft der Einbruch die Wirtschaft hart.', 'bad'); press7(OUTLETS[1].n, 'Börsenbeben: Spekulationsblase geplatzt', -1); S.shockGr -= .4; att = true; }
  }
  const G = (A().woG ??= {});
  for (const [dir, n] of [[-1, 'staat'], [1, 'markt']] as [number, string][]) if (!G[n] && v * dir >= 50) {
    G[n] = S.m; A().polar = clamp((A().polar || 0) + 8, 0, 100); S.basis -= .5;
    if (dir < 0) { S.bonus.unt -= 2; log('Ein Gegenlager organisiert sich: Unternehmer, Mittelstand und liberale Initiativen gründen ein Bündnis gegen den Staatskurs.', 'bad'); press7(OUTLETS[1].n, 'Bündnis „Freiheit statt Staat“ mobilisiert gegen die Regierung', -.8); }
    else { S.bonus.arb -= 2; log('Ein Gegenlager organisiert sich: Gewerkschaften, Sozialverbände und Kirchen schließen sich gegen den Marktkurs zusammen.', 'bad'); press7(OUTLETS[2].n, 'Bündnis „Sozialstaat jetzt“ mobilisiert gegen die Regierung', -.8); }
    att = true;
  }
  const eu = A().woEu || [];
  if (eu.length) A().woEu = eu.filter((x: any) => {
    if (S.m < x.m) return true;
    log(`Brüssel leitet ein Vertragsverletzungsverfahren wegen „${x.t}“ ein: Verstoß gegen Beihilfe- und Binnenmarktrecht. Investoren werden vorsichtiger.`, 'bad');
    press7(OUTLETS[1].n, `EU-Kommission geht gegen „${x.t}“ vor`, -.8); S.shockGr -= .12; S.shockB -= 2; S.basis -= .3; att = true; return false;
  });
  return att;
}

/** Anzeige: Wirkungen der aktuellen Ordnung. */
export function woEffects() {
  const v = wo();
  return { gr: woGr(), gini: woGini(), flight: v < -35, strikes: v > 35, crash: v > 35, lobby: v < -35 ? 'Wirtschaftsverbände' : v > 35 ? 'Gewerkschaften und Sozialverbände' : null, x: Math.abs(v) - 35, de: de(v, 0) };
}
